/* eslint-disable prefer-destructuring */
import { FormatXlsxNumberProps } from 'components/SpreadsheetNotes/utilities/FormatXlsxNumber/FormatXlsxNumber.props';

// Regex constants extracted for clarity and reuse
// Matches quoted literals like "text" and captures the inner content

const QUOTED_LITERAL_REGEX = /"([^"]*)"/g;
// Matches escaped literal characters like \x and captures the character
const ESCAPED_LITERAL_REGEX = /\\(.)/g;
// Matches the fill character modifier *x (single, for first match)
const FILL_CHAR_MODIFIER_SINGLE_REGEX = /\*(.)/;
// Matches all fill character modifiers *x globally to remove them
const FILL_CHAR_MODIFIER_GLOBAL_REGEX = /\*(.)/g;
// Matches spacing placeholder like _)
const SPACING_PLACEHOLDER_REGEX = /_(.)/g;
// Matches an Excel-like number token (e.g., #,##0, 0.00, #,##0.0)
const NUMBER_TOKEN_REGEX = /[#0,]+(?:\.[0#]+)?/;
// Matches zeros for counting decimal places in the fractional part
const ZERO_CHAR_REGEX = /0/g;
// Inserts thousands separators at the proper boundaries
//
// Captures a numeric sequence to split around for fill insertion
const NUMBER_CAPTURE_FOR_FILL_SPLIT_REGEX = /(\d[\d,]*\.?\d*)/;


// safer alternative than regex
const addThousandsSeparator = (numStr: string): string => {
  const parts = numStr.split('.');
  const integerPart = parts[0];
  const decimalPart = parts[1];

  // Add separators from right to left
  let result = '';
  for (let i = integerPart.length - 1, count = 0; i >= 0; i--, count++) {
    if (count > 0 && count % 3 === 0) {
      result = `,${  result}`;
    }
    result = integerPart[i] + result;
  }

  return decimalPart ? `${result  }.${  decimalPart}` : result;
};


export const FormatXlsxNumber = ({ value, format }: FormatXlsxNumberProps) => {
  // Length check
  if (format.length > 500) {
    return String(value);
  }

  // Split format into sections: positive;negative;zero;text
  const sections = format.split(';');

  let formatToUse = sections[0]; // default to positive format

  // Determine which format section to use
  let workingValue = value;
  if (workingValue < 0 && sections[1]) {
    formatToUse = sections[1];
    workingValue = Math.abs(workingValue); // work with absolute value for negative formatting
  } else if (workingValue === 0 && sections[2]) {
    formatToUse = sections[2];
  }

  // Work on a processed copy for replacements
  let result = formatToUse;

  // Handle quoted literals first (before other processing)
  result = result.replace(QUOTED_LITERAL_REGEX, '$1');

  // Handle literal characters (escaped with \\)
  result = result.replace(ESCAPED_LITERAL_REGEX, '$1');

  // Handle fill character modifier (*) - must be done before spacing placeholders
  let fillChar: string | null = null;
  let fillCount = 0;
  const fillMatch = result.match(FILL_CHAR_MODIFIER_SINGLE_REGEX);
  if (fillMatch) {
    fillChar = fillMatch[1];
    // Remove the fill modifier from the format for now
    result = result.replace(FILL_CHAR_MODIFIER_GLOBAL_REGEX, '');
  }

  // Handle spacing placeholders (_) - convert to actual spaces
  // Map common currency symbols to approximate widths (in spaces)
  const symbolWidths: Record<string, number> = {
    '€': 1,
    '$': 1,
    '£': 1,
    '¥': 1,
    '₹': 1,
    '₽': 1,
    // Add more as needed
  };

  result = result.replace(SPACING_PLACEHOLDER_REGEX, (_match, char) => {
    const width = symbolWidths[char] || 1;
    return ' '.repeat(width);
  });

  // Build an analysis copy to detect tokens (quotes/escapes already removed above)
  const analysis = result;

  // Detect if percent symbol exists in the format (outside of quotes; we've removed them)
  const hasPercent = analysis.includes('%');

  // Find the number token (e.g., #,##0, 0.00, #,##0.0 etc.). Do not include % in the token
  const tokenMatch = analysis.match(NUMBER_TOKEN_REGEX);

  if (tokenMatch) {
    const token = tokenMatch[0];

    // Determine decimal places from zeros in fractional part
    let decimalPlaces = 0;
    const dotIdx = token.indexOf('.');
    if (dotIdx !== -1) {
      const fractional = token.slice(dotIdx + 1);
      decimalPlaces = (fractional.match(ZERO_CHAR_REGEX) || []).length;
    }

    // Compute the numeric value to format
    const scaledValue = hasPercent ? workingValue * 100 : workingValue;

    // Format the number with fixed decimals
    let formattedNum = scaledValue.toFixed(decimalPlaces);

    // Add thousands separators if pattern includes commas in the integer part
    if (token.includes(',')) {
      formattedNum = addThousandsSeparator(formattedNum);
    }

    // Replace only the first occurrence of the number token within result
    result = result.replace(NUMBER_TOKEN_REGEX, formattedNum);
  }

  // Apply fill character if present
  if (fillChar && fillCount >= 0) {
    // For web display, we'll add a modest amount of fill characters
    // In a real spreadsheet, this would fill to the cell width
    //   since we don't know the width, perhaps we want to move this down to a smaller value or just keep it at 0?
    //   or perhaps we can insert a special character and then split the result and float one bit to the left?
    const defaultFillCount = 1; // Adjust as needed
    fillCount = defaultFillCount;

    // Find where to insert the fill
    // Typically fills go between prefix and number, or number and suffix
    const parts = result.split(NUMBER_CAPTURE_FOR_FILL_SPLIT_REGEX);
    if (parts.length === 3) {
      // Format: prefix + number + suffix
      result = parts[0] + fillChar.repeat(fillCount) + parts[1] + parts[2];
    } else if (parts.length === 1) {
      // No number found, just add fill at the end
      result += fillChar.repeat(fillCount);
    } else {
      // Multiple numbers or complex format, add fill at the end
      result += fillChar.repeat(fillCount);
    }
  }

  return result;
};
